/**
 * Utility functions for NOVA classification and display
 */

/**
 * Get NOVA image path based on NOVA score
 * @param {number} novaScore - NOVA score (0-4)
 * @returns {string} Path to NOVA image
 */
export const getNovaImagePath = (novaScore) => {
  // Ensure score is between 0-4, default to 0 (unknown)
  const score = typeof novaScore === 'number' && novaScore >= 0 && novaScore <= 4 
    ? novaScore 
    : 0;
  const base = typeof process !== 'undefined' && process.env?.PUBLIC_URL ? process.env.PUBLIC_URL : '';
  return `${base}/assets/nova/nova-all-group-${score}-bg.svg`;
};

/**
 * Calculate NOVA score for a recipe based on its components/ingredients
 * This is a simplified version of the NovaCalculator state machine
 * @param {Array} components - Array of recipe components/ingredients
 * @returns {number} NOVA score (0-4, where 0 = unknown)
 */
export const calculateRecipeNova = (components) => {
  if (!Array.isArray(components) || components.length === 0) {
    return 0; // unknown
  }

  // State machine states (matching NovaCalculator.php)
  const ST_INIT = 0;
  const ST_UNPROCESSED = 1;
  const ST_CULINARY = 2;
  const ST_PROCESSED = 3;
  const ST_ULTRAPROCESSED = 4;
  const ST_UNKNOWN = 5;
  const ST_CRASH = -1;

  let state = ST_INIT;
  let hasUnknownIngredient = false;

  for (const content of components) {
    let code = "0000"; // default unknown

    // Get NOVA flag from ingredient
    // For components, we expect them to already have novaflag or nova calculated
    // If not, we'll try to get it from nested ingredient/product data
    let novaflag = null;
    
    if (content.ingredient_id && content.ingredient) {
      // Get from ingredient novaflag
      novaflag = content.ingredient.novaflag ?? content.novaflag ?? null;
      // Check for fortification role (should be treated as unprocessed in calculation)
      const novarole = content.ingredient.novarole 
        ? content.ingredient.novarole.toLowerCase() 
        : null;
      
      if (novarole === 'fortification') {
        novaflag = 1; // treat as unprocessed
      }
    } else if (content.novaflag !== undefined && content.novaflag !== null) {
      novaflag = content.novaflag;
    } else if (content.nova !== undefined && content.nova !== null) {
      // Product NOVA (already calculated)
      // Convert to novaflag-like value: 1=unprocessed, 2=culinary, 3=processed, 4=ultra-processed
      novaflag = content.nova;
    }

    // Build 4-bit code from novaflag
    // 0 => "0000" unknown
    // 1 => "1000" unprocessed
    // 2 => "0100" culinary
    // 3 => "1100" processed
    // 4 => "0010" ultra-processed
    if (novaflag !== null && novaflag !== undefined) {
      let j = parseInt(novaflag, 10);
      if (isNaN(j) || j < 0 || j > 4) {
        code = "0000";
      } else {
        code = "";
        for (let i = 0; i < 4; i++) {
          code += (j & 1) === 1 ? "1" : "0";
          j >>= 1;
        }
      }
    }

    // Track unknown ingredients
    if (code === "0000") {
      hasUnknownIngredient = true;
    }

    // State machine transition
    const stateCode = `ST_${state}_C_${code}`;
    let nextState = state; // default: unchanged

    switch (stateCode) {
      // INITIAL STATE
      case "ST_0_C_0000":
        nextState = ST_UNKNOWN;
        break;
      case "ST_0_C_1000":
        nextState = ST_UNPROCESSED;
        break;
      case "ST_0_C_0100":
        nextState = ST_CULINARY;
        break;
      case "ST_0_C_1100":
        nextState = ST_PROCESSED;
        break;
      case "ST_0_C_0010":
        nextState = ST_ULTRAPROCESSED;
        break;

      // UNPROCESSED
      case "ST_1_C_0000":
        nextState = ST_UNKNOWN;
        break;
      case "ST_1_C_0100":
        nextState = ST_PROCESSED;
        break;
      case "ST_1_C_1100":
        nextState = ST_PROCESSED;
        break;
      case "ST_1_C_0010":
        nextState = ST_ULTRAPROCESSED;
        break;
      // ST_1_C_1000: unchanged

      // CULINARY
      case "ST_2_C_0000":
        nextState = ST_UNKNOWN;
        break;
      case "ST_2_C_1000":
        nextState = ST_PROCESSED;
        break;
      case "ST_2_C_1100":
        nextState = ST_PROCESSED;
        break;
      case "ST_2_C_0010":
        nextState = ST_ULTRAPROCESSED;
        break;
      // ST_2_C_0100: unchanged

      // PROCESSED
      case "ST_3_C_0000":
        nextState = ST_UNKNOWN;
        break;
      case "ST_3_C_0010":
        nextState = ST_ULTRAPROCESSED;
        break;
      // ST_3_C_1000, ST_3_C_0100, ST_3_C_1100: unchanged

      // ULTRAPROCESSED
      case "ST_4_C_0000":
        // Stay ultra-processed
        break;
      // All other cases in ultra-processed: unchanged

      // UNKNOWN
      case "ST_5_C_0010":
        nextState = ST_ULTRAPROCESSED;
        break;
      // All other cases in unknown: unchanged

      default:
        // Unknown state code - should not happen
        break;
    }

    state = nextState;

    if (state === ST_CRASH) {
      break;
    }
  }

  // Convert final state to NOVA score
  let nova = 0; // unknown
  switch (state) {
    case ST_UNPROCESSED:
      nova = 1;
      break;
    case ST_CULINARY:
      nova = 2;
      break;
    case ST_PROCESSED:
      nova = 3;
      break;
    case ST_ULTRAPROCESSED:
      nova = 4;
      break;
    case ST_UNKNOWN:
    default:
      nova = 0;
      break;
  }

  // Global rule: if ANY ingredient is unknown, override to NOVA = 0
  if (hasUnknownIngredient) {
    nova = 0;
  }

  return nova;
};

/**
 * Get NOVA score from a component (ingredient, product, or recipe)
 * @param {Object} component - Component object with source_type and related data
 * @returns {number} NOVA score (0-4, where 0 = unknown)
 */
export const getNovaScore = (component) => {
  if (!component) {
    return 0; // unknown
  }

  const sourceType = component.source_type || component.sourceType || component.type;

  if (sourceType === 'ingredient') {
    // For ingredients, use novaflag from ingredient
    const ingredient = component.ingredient || {};
    const novaflag = component.novaflag ?? ingredient.novaflag ?? null;
    
    if (novaflag !== null && novaflag !== undefined) {
      const score = parseInt(novaflag, 10);
      // Ensure valid range
      return (score >= 0 && score <= 4) ? score : 0;
    }
    
    return 0; // unknown if no novaflag
  } else if (sourceType === 'product') {
    // For products, use nova field (pre-calculated)
    const product = component.product || {};
    const nova = component.nova ?? product.nova ?? null;
    
    if (nova !== null && nova !== undefined) {
      const score = parseInt(nova, 10);
      // Ensure valid range
      return (score >= 0 && score <= 4) ? score : 0;
    }
    
    return 0; // unknown if no nova
  } else if (sourceType === 'recipe') {
    // For recipes, calculate from components
    // Try to get pre-calculated nova first
    const dish = component.dish || {};
    const preCalcNova = component.nova ?? dish.nova ?? null;
    
    if (preCalcNova !== null && preCalcNova !== undefined) {
      const score = parseInt(preCalcNova, 10);
      if (score >= 0 && score <= 4) {
        return score;
      }
    }

    // If no pre-calculated, calculate from components
    // Components might be in component.components, component.ingredients, or component.dish.ingredients
    const recipeComponents = component.components 
      || component.ingredients 
      || (dish.ingredients ? dish.ingredients : [])
      || [];

    if (recipeComponents.length > 0) {
      return calculateRecipeNova(recipeComponents);
    }

    return 0; // unknown if no components
  }

  return 0; // unknown for unknown source types
};

/**
 * Get NOVA image path for a component
 * @param {Object} component - Component object
 * @returns {string} Path to NOVA image
 */
export const getComponentNovaImage = (component) => {
  const novaScore = getNovaScore(component);
  return getNovaImagePath(novaScore);
};

